import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useForm, FormProvider } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers'
import { useQuery, gql } from '@apollo/client'
import { useAuth } from 'lib/auth'
import VerifyAccessCode from 'components/verify-access-code'
import { COACHING_CENTER_SLUGS } from 'utils/constants'
import { stripPhoneNumber } from 'utils/phoneNumber'
import RegistrationWithPaymentIntents from '../registration-with-payment-intents'
import { flags, useFeatureFlags } from 'lib/feature-flags'
import { amplitudeEvent } from 'lib/amplitude'
import {
  GROUP_EVENT_REGISTRATION_VIEW_EVENT,
  groupEventRegistrationExperimentProps,
} from '../registration-experiment'

const GET_STUDENT_FOR_GROUP_EVENT_REGISTRATION = gql`
  query StudentForGroupEventRegistration($id: ID!) {
    student(id: $id) {
      id
      isMinor
      firstName
      lastName
      contact {
        email
        phone
      }
    }
  }
`

const optionalPhoneNumber = yup.string().matches(/^\d{3}-?\d{3}-?\d{4}$/, {
  message: 'Please enter a valid phone number',
  excludeEmptyString: true,
})

const requiredPhoneNumber = yup
  .string()
  .required('This field is required')
  .matches(/^\d{3}-?\d{3}-?\d{4}$/, {
    message: 'Please enter a valid phone number',
  })

const validationSchema = (
  shouldShowWaiver = false,
  { requireContactPhone = false } = {},
) =>
  yup.object().shape({
    useFirstParticipantContactInformation: yup.boolean().required(),
    contact: yup.object().when('useFirstParticipantContactInformation', {
      is: false,
      then: yup
        .object()
        .shape({
          firstName: yup.string().required('This field is required'),
          lastName: yup.string().required('This field is required'),
          email: yup
            .string()
            .email('Please enter a valid email address')
            .required('This field is required'),
          phoneNumber: requireContactPhone
            ? requiredPhoneNumber
            : optionalPhoneNumber,
        })
        .required(),
    }),
    participants: yup
      .array()
      .of(
        yup.object().shape({
          firstName: yup.string().required('This field is required'),
          lastName: yup.string().required('This field is required'),
          email: yup.string().email('Please enter a valid email address'),
          phoneNumber: optionalPhoneNumber,
          type: yup
            .string()
            .oneOf(['Adult', 'Junior'])
            .required(),
        }),
      )
      .required('Please register at least one participant')
      .min(1, 'Please register at least one participant')
      .when('useFirstParticipantContactInformation', {
        is: true,
        then: schema => {
          let next = schema.test(
            'first-participant-email-required',
            'This field is required',
            function (val) {
              return ((val && val.length > 0 && val[0].email) || '').length ===
                0
                ? this.createError({ path: 'participants[0].email' })
                : true
            },
          )
          if (requireContactPhone) {
            next = next.test(
              'first-participant-phone-required',
              'This field is required',
              function (val) {
                return ((val && val.length > 0 && val[0].phoneNumber) || '')
                  .length === 0
                  ? this.createError({ path: 'participants[0].phoneNumber' })
                  : true
              },
            )
          }
          return next
        },
      }),
    ...(shouldShowWaiver && {
      agreeWaiver: yup
        .boolean()
        .oneOf([true], 'You must agree to the waiver to continue'),
    }),
  })

const normalizePhoneNumberForValidation = phoneNumber => {
  const digits = stripPhoneNumber(phoneNumber || '').replace(/\D/g, '')
  if (digits.length === 11 && digits.startsWith('1')) {
    return digits.slice(1)
  }
  return digits.length > 0 ? digits : ''
}

const GroupEventRegistrationForm = ({ groupEvent, studentId }) => {
  const { user, isConsumer } = useAuth()
  const [requireContactPhone] = useFeatureFlags([
    flags.FLAG_EXP_GROUP_EVENT_REQUIRE_CONTACT_PHONE,
  ])
  const isAuthenticatedConsumer = user && isConsumer
  const coachRegisterOnBehalfOfStudent =
    user?.externalId === groupEvent?.coach?.externalId

  const canSkipPayment = user && user?.id === groupEvent?.coach?.id
  // TODO - paymentRequired can be folded into form state for easier handling of validation
  const [paymentRequired, setPaymentRequired] = useState(
    !canSkipPayment && groupEvent.acceptsOnlinePayments,
  )
  const [isAccessCodeVerified, setIsAccessCodeVerified] = useState(false)
  const requiresAccessCode = groupEvent.requiresAccessCode
  const isCoachingCenterGE = COACHING_CENTER_SLUGS.includes(
    groupEvent?.academy?.slug,
  )
  const shouldShowWaiver = isCoachingCenterGE && !coachRegisterOnBehalfOfStudent
  const [marketingOptInChecked, setMarketingOptInChecked] = useState(false)
  const handleMarketingOptInChange = isChecked => {
    setMarketingOptInChecked(isChecked)
  }

  const shouldPrefillFromStudentId =
    Boolean(studentId) && coachRegisterOnBehalfOfStudent

  const prefillGuardRef = useRef(null)

  const { data: studentData } = useQuery(
    GET_STUDENT_FOR_GROUP_EVENT_REGISTRATION,
    {
      variables: { id: studentId },
      skip: !shouldPrefillFromStudentId,
    },
  )

  const form = useForm({
    defaultValues: {
      contact: isAuthenticatedConsumer
        ? {
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email,
          phoneNumber: normalizePhoneNumberForValidation(user.phoneNumber),
        }
        : {},
      participants: isAuthenticatedConsumer
        ? [
          {
            firstName: user.firstName,
            lastName: user.lastName,
            email: user.email,
            phoneNumber: normalizePhoneNumberForValidation(user.phoneNumber),
            type: 'Adult',
          },
        ]
        : [],
      useFirstParticipantContactInformation: false,
      ...(shouldShowWaiver && {
        agreeWaiver: false,
      }),
    },
    resolver: yupResolver(
      validationSchema(shouldShowWaiver, { requireContactPhone }),
    ),
  })

  useEffect(() => {
    amplitudeEvent(
      GROUP_EVENT_REGISTRATION_VIEW_EVENT,
      groupEventRegistrationExperimentProps({
        requireContactPhone,
        groupEvent,
      }),
    )
  }, [groupEvent, requireContactPhone])

  useEffect(() => {
    if (!shouldPrefillFromStudentId) return
    const student = studentData?.student
    if (!student) return

    const prefillKey = `${studentId}:${shouldShowWaiver}`
    if (prefillGuardRef.current === prefillKey) return
    prefillGuardRef.current = prefillKey

    const participantType = student.isMinor ? 'Junior' : 'Adult'
    const phone = normalizePhoneNumberForValidation(student.contact?.phone)
    const email = student.contact?.email || ''

    const participant = {
      firstName: student.firstName || '',
      lastName: student.lastName || '',
      email,
      phoneNumber: phone,
      type: participantType,
    }

    form.reset({
      contact: {
        firstName: student.firstName || '',
        lastName: student.lastName || '',
        email,
        phoneNumber: phone,
      },
      participants: [participant],
      useFirstParticipantContactInformation: participantType === 'Adult',
      ...(shouldShowWaiver && {
        agreeWaiver: false,
      }),
    })
  }, [
    form,
    studentId,
    shouldPrefillFromStudentId,
    studentData,
    shouldShowWaiver,
  ])

  return (
    <>
      <FormProvider {...form}>
        <RegistrationWithPaymentIntents
          groupEvent={groupEvent}
          shouldShowWaiver={shouldShowWaiver}
          paymentRequired={paymentRequired}
          setPaymentRequired={setPaymentRequired}
          marketingOptInChecked={marketingOptInChecked}
          handleMarketingOptInChange={handleMarketingOptInChange}
        />
      </FormProvider>
      <VerifyAccessCode
        groupEvent={groupEvent}
        isOpen={requiresAccessCode && !isAccessCodeVerified}
        setIsOpen={setIsAccessCodeVerified}
        coachName={groupEvent?.coach?.name}
        setVerifiedAccessCode={setIsAccessCodeVerified}
        canRequestAccessCode={!isCoachingCenterGE}
      />
    </>
  )
}

export default GroupEventRegistrationForm
export { validationSchema, normalizePhoneNumberForValidation }

GroupEventRegistrationForm.propTypes = {
  groupEvent: PropTypes.object.isRequired,
  studentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
}
